import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Store variant info before replacing
    const variant = event.detail.resource;
    const variantMediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : null;
    const currentMediaId = getCurrentVisibleMediaId(this);

    if (!variantMediaId && currentMediaId) return;

    this.replaceWith(newMediaGallery);

    // Filter grid items after the new gallery is connected
    requestAnimationFrame(() => {
      filterVariantImages(/** @type {HTMLElement} */ (newMediaGallery), variantMediaId);
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    const sourceMedia = getEventMedia(event);
    const dialogMedia = this.refs.zoomDialogComponent?.refs?.media ?? [];
    const mediaIndex = getMediaIndex(dialogMedia, sourceMedia);

    this.refs.zoomDialogComponent?.open(mediaIndex >= 0 ? mediaIndex : index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Returns identifying data for the clicked/tapped gallery item.
 *
 * @param {PointerEvent} event - The pointer event.
 * @returns {{ mediaId: string | null, imageUrl: string | null }}
 */
function getEventMedia(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return { mediaId: null, imageUrl: null };

  const mediaContainer = target.closest('li, slideshow-slide, .product-media-container') ?? target;
  const mediaElement = target.closest('[data-media-id]') ?? mediaContainer.querySelector('[data-media-id]');
  const image = target.closest('img') ?? mediaContainer.querySelector('img');

  return {
    mediaId: mediaElement instanceof HTMLElement ? mediaElement.dataset.mediaId ?? null : null,
    imageUrl: getImageUrl(image),
  };
}

/**
 * Finds the current zoom-dialog media index for a clicked media signature.
 *
 * @param {HTMLElement[]} mediaItems - The zoom-dialog media refs.
 * @param {{ mediaId: string | null, imageUrl: string | null }} sourceMedia - The clicked media signature.
 * @returns {number}
 */
function getMediaIndex(mediaItems, sourceMedia) {
  if (sourceMedia.imageUrl) {
    const imageIndex = mediaItems.findIndex((item) => normalizeImageUrl(getImageUrl(item.querySelector('img'))) === sourceMedia.imageUrl);
    if (imageIndex >= 0) return imageIndex;
  }

  if (!sourceMedia.mediaId) return -1;

  return mediaItems.findIndex((item) => {
    if (item.dataset.mediaId === sourceMedia.mediaId) return true;

    const nestedMedia = item.querySelector('[data-media-id]');
    return nestedMedia instanceof HTMLElement && nestedMedia.dataset.mediaId === sourceMedia.mediaId;
  });
}

/**
 * Returns a normalized image URL for matching app-swapped gallery images.
 *
 * @param {Element | null} image - The image element.
 * @returns {string | null}
 */
function getImageUrl(image) {
  if (!(image instanceof HTMLImageElement)) return null;

  return normalizeImageUrl(
    image.getAttribute('data_max_resolution') ||
      image.currentSrc ||
      image.src ||
      image.getAttribute('srcset')?.split(',').pop()?.trim().split(/\s+/)[0] ||
      null
  );
}

/**
 * Normalizes Shopify image URLs so different width variants match the same source image.
 *
 * @param {string | null} src - The image URL.
 * @returns {string | null}
 */
function normalizeImageUrl(src) {
  if (!src) return null;

  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.delete('width');
    return url.pathname + '?' + url.searchParams.toString();
  } catch (e) {
    return src.replace(/([?&])width=\d+(&)?/, '$1').replace(/[?&]$/, '');
  }
}

/**
 * Returns the media id currently shown in the gallery.
 *
 * @param {HTMLElement} mediaGallery - The current media gallery element.
 * @returns {string | null}
 */
function getCurrentVisibleMediaId(mediaGallery) {
  const activeSlideMedia = mediaGallery.querySelector(
    'slideshow-slide[aria-hidden="false"]:not([hidden]) [data-media-id]'
  );

  if (activeSlideMedia instanceof HTMLElement && activeSlideMedia.dataset.mediaId) {
    return activeSlideMedia.dataset.mediaId;
  }

  const firstVisibleMedia = Array.from(mediaGallery.querySelectorAll('[data-media-id]')).find((media) => {
    if (!(media instanceof HTMLElement)) return false;

    const hiddenParent = media.closest('[hidden], [style*="display: none"]');
    return !hiddenParent;
  });

  return firstVisibleMedia instanceof HTMLElement ? firstVisibleMedia.dataset.mediaId ?? null : null;
}

/**
 * Filters grid items and slideshow slides to show only images matching the variant's featured media alt text.
 *
 * @param {HTMLElement} mediaGallery - The media gallery element to filter.
 * @param {string | null} variantMediaId - The ID of the variant's featured media.
 */
function filterVariantImages(mediaGallery, variantMediaId) {
  // Find the variant's featured media element to get its alt text
  const variantMediaElement = getVariantMediaElement(mediaGallery, variantMediaId);
  
  if (!variantMediaElement) return;

  let variantAltText = null;

  // Try to get alt text from grid (li[data-alt])
  const grid = /** @type {HTMLElement | null} */ (mediaGallery.querySelector('.media-gallery__grid'));
  if (grid) {
    const variantLi = /** @type {HTMLElement | null} */ (variantMediaElement.closest('li[data-alt]'));
    if (variantLi) {
      variantAltText = variantLi.dataset.alt;
    }
  }

  // If not found in grid, try to get from slideshow slide (data-image-alt)
  if (!variantAltText) {
    const variantSlide = /** @type {HTMLElement | null} */ (
      variantMediaElement.closest('slideshow-slide[data-image-alt]')
    );
    if (variantSlide) {
      variantAltText = variantSlide.dataset.imageAlt;
    }
  }

  if (!variantAltText) return;

  // Filter grid items: show only those with matching alt text
  if (grid) {
    const gridItems = Array.from(grid.querySelectorAll('li[data-alt]'));
    const hasGridMatch = gridItems.some((item) => /** @type {HTMLElement} */ (item).dataset.alt === variantAltText);
    if (hasGridMatch) {
      gridItems.forEach((item) => {
        const itemElement = /** @type {HTMLElement} */ (item);
        if (itemElement.dataset.alt === variantAltText) {
          itemElement.style.display = '';
          itemElement.removeAttribute('hidden');
        } else {
          itemElement.style.display = 'none';
          itemElement.setAttribute('hidden', '');
        }
      });
    }
  }

  // Filter slideshow slides: show only those with matching alt text
  const slides = /** @type {NodeListOf<HTMLElement>} */ (
    mediaGallery.querySelectorAll('slideshow-slide[data-image-alt]')
  );

  if (slides.length > 0) {
      const hasSlideMatch = Array.from(slides).some((slide) => slide.dataset.imageAlt === variantAltText);
    if (hasSlideMatch) {
      slides.forEach((slide) => {
        if (slide.dataset.imageAlt === variantAltText) {
          slide.removeAttribute('hidden');
        } else {
          slide.setAttribute('hidden', '');
        }

        slide.setAttribute('aria-hidden', 'true');
      });

      const slideshowElement = mediaGallery.querySelector('slideshow-component');
      const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
        slideshowElement instanceof HTMLElement ? slideshowElement : null
      );
      if (slideshow && slideshow.select) {
        requestAnimationFrame(() => {
          const visibleSlides = Array.from(slides).filter(
            (slide) => !slide.hasAttribute('hidden')
          );
          if (visibleSlides.length > 0) {
            visibleSlides[0].setAttribute('aria-hidden', 'false');
            slideshow.select({ id: visibleSlides[0].getAttribute('slide-id') ?? '' }, undefined, { animate: false });
          }
        });
      }
    }
  }
}

/**
 * Finds the selected variant media in the newly rendered gallery.
 *
 * @param {HTMLElement} mediaGallery - The media gallery element to search.
 * @param {string | null} variantMediaId - The ID of the selected variant media.
 * @returns {HTMLElement | null}
 */
function getVariantMediaElement(mediaGallery, variantMediaId) {
  if (variantMediaId) {
    const mediaElement = mediaGallery.querySelector(`[data-media-id="${CSS.escape(variantMediaId)}"]`);
    if (mediaElement instanceof HTMLElement) return mediaElement;
  }

  return mediaGallery.querySelector('[data-media-id]') instanceof HTMLElement
    ? /** @type {HTMLElement} */ (mediaGallery.querySelector('[data-media-id]'))
    : null;
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
